import { useEffect } from 'react';
import { Navigate, Route, useParams } from 'react-router';
import { Col, Row } from 'antd';
import { useMutation } from '@apollo/client/react';

import CustomerMenu from './CustomerMenu';
import CustomerMeta from './CustomerMeta/CustomerMeta';
import {
  SidebarPortal,
  SidebarDivider,
  SidebarScrollContainer,
} from 'layouts/DashboardLayout/Sidebar';

import AccountHistory from 'components/AccountHistory/AccountHistory';
import Interactions from 'components/Interactions/Interactions';
import StickyNotes from 'components/StickyNotes/StickyNotes';

import AttachmentsPage from 'pages/AttachmentsPage/AttachmentsPage';
import CreditAccountPage from 'pages/CreditAccountPage/CreditAccountPage';
import CreditApplicationPage from 'pages/CreditApplicationPage/CreditApplicationPage';
import CustomerDetailsPage from 'pages/CustomerDetailsPage/CustomerDetailsPage';
import CustomerOverviewPage from 'pages/CustomerOverviewPage/CustomerOverviewPage';
import FraudCasesPage from 'pages/FraudCasesPage/FraudCasesPage';
import FundingAccountsPage from 'pages/FundingAccountsPage/FundingAccountsPage';
import PreferencesPage from 'pages/PreferencesPage/PreferencesPage';

import AccountsInfoProvider from 'providers/AccountsInfoProvider';

import useAgent from 'hooks/useAgent';
import useCustomerInfo from 'hooks/useCustomerInfo';

import { ADD_CUSTOMER_VIEW } from 'mutations/notes';

import { SentryRoutes } from 'services/sentry';

const CustomerLayout = () => {
  const { customerId } = useParams();
  const { customerInfo } = useCustomerInfo();
  const agent = useAgent();

  const [addCustomerView] = useMutation(ADD_CUSTOMER_VIEW);

  useEffect(() => {
    const agentId = agent?.user?.requester_id;
    const agentName = agent?.user?.name;
    if (agentId && agentName) {
      addCustomerView({
        variables: {
          customerId,
          agentId: agent?.user?.requester_id,
          agentName: agent?.user?.name,
        },
      });
    }
  }, [agent, customerId]);

  return (
    <AccountsInfoProvider>
      <title>{customerInfo?.fullName}</title>

      <title>
        {customerInfo?.fullName
          ? `${customerInfo.fullName} | Customer`
          : 'Customer'}
      </title>

      <SidebarPortal>
        <SidebarScrollContainer>
          <CustomerMeta />
          <SidebarDivider />
          <CustomerMenu />
        </SidebarScrollContainer>
      </SidebarPortal>

      <StickyNotes />
      <SentryRoutes>
        <Route index element={<Navigate replace to="accounts-overview" />} />
        <Route path="details" element={<CustomerDetailsPage />} />
        <Route path="attachments" element={<AttachmentsPage />} />
        <Route path="funding-accounts" element={<FundingAccountsPage />} />
        <Route path="accounts-overview/*" element={<CustomerOverviewPage />} />
        <Route
          path="accounts/credit/:accountId/:view?"
          element={<CreditAccountPage />}
        />
        <Route
          path="applications/:applicationId"
          element={<CreditApplicationPage />}
        />
        <Route path="preferences" element={<PreferencesPage />} />
        <Route path="fraud/*" element={<FraudCasesPage />} />
      </SentryRoutes>
      <Row
        gutter={[8, 8]}
        style={{
          marginTop: 'var(--spacing-xs)',
        }}
      >
        <Col md={24} lg={12}>
          <Interactions />
        </Col>
        <Col md={24} lg={12}>
          <AccountHistory />
        </Col>
      </Row>
    </AccountsInfoProvider>
  );
};

export default CustomerLayout;
